import * as React from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  Search,
  LogOut,
  Settings,
  Sun,
  Moon,
  Monitor,
  Plus,
  Check,
  ChevronsUpDown,
  Download,
} from "lucide-react";
import {
  CommandPalette,
  useCommandPaletteStore,
  useCommandPaletteShortcut,
} from "@/components/command-palette";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useIsDesktop } from "@/hooks/use-media-query";
import { api } from "@/lib/convex-api";
import { organizationRoute } from "@/lib/app-routes";
import { organizationContext } from "@/lib/organization-context";
import {
  usePwaInstall,
  getPwaInstallDiagnostics,
} from "@/lib/pwa/use-pwa-install";
import { useThemeStore } from "@/stores/theme-store";
import { OrganizationSettings } from "@/features/settings/organization-settings";
import { UserSettings } from "@/features/settings/user-settings";
import { NotificationsBell } from "@/features/notifications/notifications-bell";
import type { Id } from "../../../backend/convex/_generated/dataModel";
import { useAuthActions } from "@convex-dev/auth/react";
import { toast } from "sonner";

interface AppShellProps {
  children: React.ReactNode;
}

type AppShellSearch = {
  cmdk?: "1";
  modal?: "org_settings" | "user_settings" | "project_settings";
};

export function AppShell({ children }: AppShellProps) {
  // Project pages render their own unified header; hide the default one.
  const isProjectRoute = useRouterState({
    select: (s) =>
      /^\/[^/]+\/(?!new-project(?:\/|$))[^/]+(?:\/|$)/.test(
        s.location.pathname,
      ),
  });
  const isDesktop = useIsDesktop();

  // Register global Cmd/Ctrl+K shortcut
  useCommandPaletteShortcut();

  return (
    <div className="flex h-dvh flex-col">
      {!isProjectRoute && (
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          <AppLogo />

          <span className="text-muted-foreground/40 text-sm">›</span>
          <OrganizationSwitcher />
          <div className="flex flex-1" />
          <div className="flex items-center gap-1">
            <SearchBar />
            <NotificationsBell />
            <UserMenu />
          </div>
        </header>
      )}
      <main className="min-h-0 flex-1 overflow-hidden">{children}</main>

      {/* Global command palette — always mounted */}
      <CommandPalette />
    </div>
  );
}

/** Logo wordmark — reused by the project header. Hidden on mobile in default variant. */
export function AppLogo({
  compact = false,
  to = "/",
  params,
  search,
  className,
  ...linkProps
}: {
  compact?: boolean;
  to?: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
} & Omit<React.ComponentProps<typeof Link>, "to" | "search" | "params"> = {}) {
  const searchReducer = React.useMemo(() => {
    if (!search) return undefined;
    // TanStack Router's route search typing is `never` unless validateSearch is defined.
    // We still want to merge runtime search state like modals/cmdk, so we keep
    // this narrowly cast here.
    return (prev: unknown) => ({
      ...(prev as Record<string, unknown>),
      ...search,
    });
  }, [search]);

  return (
    <Link
      to={to as any}
      params={params as any}
      search={searchReducer as any}
      {...linkProps}
      className={cn(
        "app-logo items-baseline gap-0 font-semibold tracking-tight",
        className,
      )}
    >
      <span
        className={cn(
          "font-['Days_One'] font-bold",
          compact ? "text-md leading-none" : "text-lg",
        )}
      >
        md
      </span>
      <span
        className={cn(
          "font-['Libre_Baskerville'] font-normal",
          compact ? "hidden text-md leading-none sm:inline" : "text-lg",
        )}
      >
        sync
      </span>
    </Link>
  );
}

/** Search / command-bar trigger — reused by the project header */
export function SearchBar() {
  const setOpen = useCommandPaletteStore((s) => s.setOpen);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => {
            if (isDesktop) {
              setOpen(true);
              return;
            }
            void (navigate as unknown as (args: any) => Promise<void>)({
              search: (prev: Record<string, unknown>) => ({
                ...prev,
                cmdk: "1",
              }),
            });
          }}
          aria-label="Search"
        >
          <Search className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Search & commands (⌘K)</TooltipContent>
    </Tooltip>
  );
}

function OrganizationSwitcher() {
  const navigate = useNavigate();
  const organizations = useQuery(api.organizations.listForUser);
  const createOrganization = useMutation(api.organizations.createOrganization);
  const isDesktop = useIsDesktop();
  const modal = useRouterState({
    select: (s) => (s.location.search as unknown as AppShellSearch)?.modal,
  });
  const [selectedId, setSelectedId] = React.useState<string | null>(
    organizationContext.getSelectedOrganizationId(),
  );
  const [settingsOpenDesktop, setSettingsOpenDesktop] = React.useState(false);
  const settingsOpen = isDesktop
    ? settingsOpenDesktop
    : modal === "org_settings";

  const setSettingsOpen = (open: boolean) => {
    if (open) {
      const selectedOrganization = organizations?.find(
        (organization) => organization.id === selectedId,
      );
      if (selectedOrganization?.role !== "owner") {
        toast.error(
          "Only organization owners can access organization settings.",
        );
        return;
      }
    }
    if (isDesktop) {
      setSettingsOpenDesktop(open);
      return;
    }
    void (navigate as unknown as (args: any) => Promise<void>)({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        modal: open ? "org_settings" : undefined,
      }),
      replace: !open,
    });
  };

  React.useEffect(() => {
    const handler = () => setSettingsOpen(true);
    globalThis.addEventListener("mdsync:open-org-settings", handler);
    return () =>
      globalThis.removeEventListener("mdsync:open-org-settings", handler);
  }, []);

  React.useEffect(() => {
    if (!organizations || organizations.length === 0) return;
    const selectedExists = selectedId
      ? organizations.some((organization) => organization.id === selectedId)
      : false;
    if (selectedExists) return;
    const fallbackId = organizations[0]!.id;
    setSelectedId(fallbackId);
    organizationContext.setSelectedOrganizationId(fallbackId);
    const fallbackOrganization = organizations.find(
      (organization) => organization.id === fallbackId,
    );
    if (!fallbackOrganization) {
      return;
    }
    void navigate({
      to: organizationRoute,
      params: { orgSlug: fallbackOrganization.slug },
    });
  }, [navigate, organizations, selectedId]);

  if (!organizations || organizations.length === 0) {
    return null;
  }

  const current =
    organizations.find((organization) => organization.id === selectedId) ??
    organizations[0];
  const canAccessOrganizationSettings = current?.role === "owner";

  const onCreateOrganization = async () => {
    const name = globalThis.prompt("Organization name", "New Organization");
    if (!name?.trim()) return;
    const nextOrganizationId = await createOrganization({
      name: name.trim(),
    });
    const id = String(nextOrganizationId);
    setSelectedId(id);
    organizationContext.setSelectedOrganizationId(id);
    const nextOrganization = organizations.find(
      (organization) => organization.id === id,
    );
    if (!nextOrganization) {
      return;
    }
    await navigate({
      to: organizationRoute,
      params: { orgSlug: nextOrganization.slug },
    });
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="secondary"
              size="sm"
              className="h-8 max-w-56 justify-start gap-2 px-2"
            >
              <span className="truncate text-xs">{current?.name}</span>
              <ChevronsUpDown className="ml-auto size-4 shrink-0 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>{current?.name}</DropdownMenuLabel>
            {canAccessOrganizationSettings ? (
              <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
                <Settings className="mr-2 size-4" />
                Organization settings
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Other organizations</DropdownMenuLabel>
            {organizations
              .filter((org) => org.id !== selectedId)
              .map((organization) => (
                <DropdownMenuItem
                  key={organization.id}
                  onClick={() => {
                    setSelectedId(organization.id);
                    organizationContext.setSelectedOrganizationId(
                      organization.id,
                    );
                    void navigate({
                      to: organizationRoute,
                      params: { orgSlug: organization.slug },
                    });
                  }}
                  className="flex items-center justify-between"
                >
                  <span className="truncate">{organization.name}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">
                    {organization.role}
                  </span>
                </DropdownMenuItem>
              ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void onCreateOrganization()}>
              <Plus className="mr-2 size-4" />
              Create organization
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <OrganizationSettings
        open={settingsOpen && canAccessOrganizationSettings}
        onOpenChange={setSettingsOpen}
        organizationId={(selectedId as Id<"workspaces"> | null) ?? null}
        onDeleted={() => {
          setSelectedId(null);
          organizationContext.clear();
        }}
      />
    </>
  );
}

/** User avatar dropdown with theme controls — reused by the project header */
export function UserMenu() {
  const navigate = useNavigate();
  const user = useQuery(api.users.me);
  const { signOut } = useAuthActions();
  const isDesktop = useIsDesktop();
  const modal = useRouterState({
    select: (s) => (s.location.search as unknown as AppShellSearch)?.modal,
  });
  const [settingsOpenDesktop, setSettingsOpenDesktop] = React.useState(false);
  const settingsOpen = isDesktop
    ? settingsOpenDesktop
    : modal === "user_settings";

  const setSettingsOpen = (open: boolean) => {
    if (isDesktop) {
      setSettingsOpenDesktop(open);
      return;
    }
    void (navigate as unknown as (args: any) => Promise<void>)({
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        modal: open ? "user_settings" : undefined,
      }),
      replace: !open,
    });
  };
  const { setTheme } = useThemeStore();
  const { canInstall, isInstalled, promptInstall } = usePwaInstall();

  const initials = (user?.name ?? "U")
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onSignOut = async () => {
    await signOut();
    await navigate({ to: "/login" });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.avatarUrl} alt="User avatar" />
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">{user?.name ?? "User"}</p>
              <p className="text-xs text-muted-foreground">
                {user?.email ?? "user@example.com"}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setTheme("light")}>
            <Sun className="mr-2 h-4 w-4" />
            Light theme
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme("dark")}>
            <Moon className="mr-2 h-4 w-4" />
            Dark theme
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme("system")}>
            <Monitor className="mr-2 h-4 w-4" />
            System theme
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {!isInstalled && (
            <DropdownMenuItem
              onClick={() => {
                if (canInstall) {
                  void promptInstall();
                  return;
                }
                const diag = getPwaInstallDiagnostics();
                if (diag.isStandalone) {
                  toast.info("Already running as installed app.");
                  return;
                }
                if (!diag.isSecureContext) {
                  toast.info(
                    "Install requires HTTPS or localhost (secure context).",
                  );
                  return;
                }
                if (
                  diag.hasManifestLink &&
                  diag.hasServiceWorkerSupport &&
                  !diag.hasCachedPrompt
                ) {
                  toast.info("Use your browser menu to install.", {
                    description:
                      'Tap the browser menu (three dots) and select "Install app" or "Add to Home screen".',
                    duration: 8000,
                  });
                  return;
                }
                toast.info("Install is not available in this browser.");
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              Install app
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <UserSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}
